import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { useSession, type SessionUser } from '@/app/store/session.store';

declare global {
  interface Window {
    __SYNCRA__?: { apiUrl?: string };
  }
}

export type AuthResponse = { accessToken: string; expiresAt: string; user: SessionUser };

const apiUrl = () => window.__SYNCRA__?.apiUrl ?? '/api/v1';

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export const http: AxiosInstance = axios.create({
  baseURL: apiUrl(),
  withCredentials: true,
  timeout: 20_000
});

http.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`);
  config.headers.set('X-Correlation-Id', crypto.randomUUID());
  return config;
});

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshing: Promise<string | null> | null = null;

/** Single-flight refresh: concurrent 401s share one refresh request. */
export function refreshSession(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios
      .post<AuthResponse>(`${apiUrl()}/auth/refresh`, null, { withCredentials: true })
      .then((res) => {
        setAccessToken(res.data.accessToken);
        useSession.getState().setSession(res.data.user, res.data.accessToken);
        return res.data.accessToken;
      })
      .catch(() => {
        setAccessToken(null);
        useSession.getState().clear();
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

const isAuthRoute = (url: string | undefined) => (url ?? '').includes('/auth/');

http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    if (error.response?.status === 401 && config && !config._retried && !isAuthRoute(config.url)) {
      config._retried = true;
      const token = await refreshSession();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
        return http.request(config);
      }
    }
    return Promise.reject(error);
  }
);
